import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { MultiListingRetailSaleModal } from 'src/components/Events/EventListing/InventoryEventListing/BulkActions/InventoryGlobalBulkActions/Actions/RetailSaleActions';
import { useAppContext } from 'src/contexts/AppContext';
import { Content } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosDropdownItem } from 'src/core/POS/PosDropdown';
import { vars } from 'src/core/themes';
import { Stack } from 'src/core/ui';
import { useUserHasAnyOfPermissions } from 'src/hooks/useUserHasAnyOfPermissions';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { SeatSaverListingWizard } from 'src/modals/SeatSaverListingWizard/SeatSaverListingWizard';
import { CartOutlineIcon } from 'src/svgs/Viagogo';
import { ContentId } from 'src/utils/constants/contentId';
import { EmptyListingQuery } from 'src/utils/eventQueryUtils';
import { Feature, Permission, PosClientConfig } from 'src/WebApiController';

import {
  rollbackCreatedSeatSavers,
  shouldRollback,
} from './addRetailSaleChain.utils';

/**
 * POS-9099 — retail-sale step of the chained "Add Retail Sale" workflow.
 *
 * Renders the (reused) MultiListingRetailSaleModal seeded with exactly the seat-saver
 * listings the chain just created, and owns the rollback decision: a successful sale flips
 * an internal committed flag via `onSaleCreated`; if this component unmounts (the modal
 * closed) without that flag set, the created seat-savers are deleted.
 *
 * The rollback lives in an unmount cleanup so it fires however the modal is dismissed
 * (Cancel, the X, click-outside) — and NOT on a completed sale, because that path sets the
 * committed flag first. `deleteSeatSaver` is itself a no-op for a listing that ended up
 * sold, so this is safe even in edge cases.
 */
const ChainedRetailSaleStep = ({
  createdListingIds,
  config,
}: {
  createdListingIds: number[];
  config: PosClientConfig;
}) => {
  const saleCommittedRef = useRef(false);

  useEffect(() => {
    return () => {
      if (shouldRollback(saleCommittedRef.current, createdListingIds)) {
        void rollbackCreatedSeatSavers(config, createdListingIds);
      }
    };
    // Runs once for the modal's lifetime; createdListingIds/config are fixed at open time
    // and the committed flag is read from a ref at cleanup, so no deps are needed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <MultiListingRetailSaleModal
      filterQuery={{ ...EmptyListingQuery, entityIds: createdListingIds }}
      onSaleCreated={() => {
        saleCommittedRef.current = true;
      }}
    />
  );
};

/**
 * POS-9099 — the whole chained "Add Retail Sale" workflow inside a SINGLE modal instance.
 *
 * Holds the step state internally and swaps only the inner content, so the modal shell
 * stays mounted the whole time — the seat-saver wizard flows straight into the retail-sale
 * step with no close/reopen (which a second `setModal` call would produce, since it would
 * tear down one subtree and mount a different one under a fresh-looking modal).
 *
 * Step 1 renders the seat-saver wizard with `onCreated`; on success we advance to step 2,
 * carrying the created listing ids. Step 2 is `ChainedRetailSaleStep`, which owns the
 * rollback. If the wizard reports zero created listings (shouldn't happen on success), we
 * just close — there's nothing to sell and nothing to roll back.
 */
const AddRetailSaleChainModal = ({ config }: { config: PosClientConfig }) => {
  const { closeModal } = useContext(ModalContext);
  const [createdListingIds, setCreatedListingIds] = useState<number[] | null>(
    null
  );

  const onInventoryCreated = useCallback(
    (ids: number[]) => {
      if (ids.length === 0) {
        closeModal(true);
        return;
      }
      setCreatedListingIds(ids);
    },
    [closeModal]
  );

  if (createdListingIds == null) {
    return <SeatSaverListingWizard onCreated={onInventoryCreated} />;
  }

  return (
    <ChainedRetailSaleStep
      createdListingIds={createdListingIds}
      config={config}
    />
  );
};

/**
 * POS-9099 — "Add Retail Sale" dropdown action.
 *
 * Chains two existing flows into one: first the Add-Placeholder (seat-saver) wizard, then
 * the retail-sale create modal seeded with just the created inventory. Mirrors
 * `LaunchAddSeatSaver` for the dropdown item + gating; gated additionally on the retail
 * feature/permission since it culminates in a retail sale. Opens a single modal
 * (`AddRetailSaleChainModal`) that transitions between the two steps internally.
 */
export const LaunchAddRetailSale = () => {
  const hasSeatSaverPermission = useUserHasAnyOfPermissions(
    Permission.Inventory_AddSeatSaver
  );
  const hasRetailCreatePermission = useUserHasAnyOfPermissions(
    Permission.Sales_CreateOfflineSale
  );
  const hasSeatSaverFeature = useUserHasFeature(Feature.SeatSaver);
  const hasRetailSaleFeature = useUserHasFeature(Feature.RetailSale);

  const { setModal } = useContext(ModalContext);
  const { activeAccountWebClientConfig } = useAppContext();

  const onLaunch = useCallback(
    (e: React.MouseEvent<HTMLDivElement | HTMLButtonElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setModal({
        children: (
          <AddRetailSaleChainModal config={activeAccountWebClientConfig} />
        ),
        clickOutsideToClose: true,
        size: 'slide-in',
      });
    },
    [setModal, activeAccountWebClientConfig]
  );

  if (
    !hasSeatSaverPermission ||
    !hasRetailCreatePermission ||
    !hasSeatSaverFeature ||
    !hasRetailSaleFeature
  ) {
    return null;
  }

  return (
    <PosDropdownItem key="AddRetailSale" onClick={onLaunch}>
      <Stack alignItems="center" gap="m" width="full">
        <CartOutlineIcon
          withHoverEffect
          fill={vars.color.textPrimary}
          size={vars.iconSize.m}
        />
        <Content id={ContentId.AddRetailSale} />
      </Stack>
    </PosDropdownItem>
  );
};
